import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { title: "Dashboard", perms: ["view_dashboard"] },
  { title: "Students", perms: ["view_students", "view_student_documents", "add_student", "edit_student"] },
  { title: "Fees", perms: ["view_fees", "add_fee_structure", "edit_fee_structure", "collect_fee", "add_discount", "void_payment"] },
  { title: "Transport", perms: ["view_transport", "edit_transport"] },
  { title: "Attendance", perms: ["view_attendance", "edit_attendance"] },
  { title: "Examinations", perms: ["view_exams", "manage_exams", "enter_marks"] },
  { title: "Expenses", perms: ["view_expenses", "add_expense"] },
  { title: "Homework", perms: ["view_homework", "manage_homework"] },
  { title: "Follow-up & Leads", perms: ["view_followup", "manage_followup", "view_leads", "manage_leads"] },
  { title: "Classes", perms: ["view_classes"] },
  { title: "Staff & Users", perms: ["view_staff", "edit_staff", "manage_users", "view_audit"] },
  { title: "Reports & Output", perms: ["view_reports", "generate_reports", "print", "download", "export"] },
  { title: "System", perms: ["manage_settings", "delete"] },
];
const labelize = (p) => p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const BULK_FIELDS = [
  ["name", "Name"], ["roll_number", "Roll No"], ["section", "Section"], ["father_name", "Father"], ["mother_name", "Mother"],
  ["phone", "Phone"], ["alt_phone", "Alt Phone"], ["dob", "DOB"], ["gender", "Gender"], ["blood_group", "Blood Group"],
  ["height", "Height"], ["weight", "Weight"], ["category", "Category"], ["house", "House"], ["caste", "Caste"],
  ["religion", "Religion"], ["aadhar_number", "Aadhar"], ["apaar_id", "Apaar ID"], ["address", "Address"], ["city", "City"],
  ["state", "State"], ["pin_code", "PIN"], ["tc_status", "TC Status"], ["status", "Status"],
];

export default function StaffPermissionsTab() {
  const [all, setAll] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkFields, setBulkFields] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  useEffect(() => {
    api.get("/settings/staff-permissions")
      .then((r) => { setAll(r.data.all || []); setPerms(r.data.permissions || []); })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/settings/staff-bulk-fields").then((r) => setBulkFields(r.data.fields || [])).catch(() => {});
  }, []);

  const toggleBulk = (k) => setBulkFields((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const saveBulk = async () => {
    setSavingBulk(true);
    try {
      const r = await api.put("/settings/staff-bulk-fields", { fields: bulkFields });
      setBulkFields(r.data.fields || []);
      toast.success("Staff bulk-edit fields saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingBulk(false); }
  };

  const toggle = (p) => setPerms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const setGroup = (gp, on) => setPerms((cur) => (on ? [...new Set([...cur, ...gp])] : cur.filter((x) => !gp.includes(x))));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/staff-permissions", { permissions: perms, apply_to_existing: true });
      toast.success(`Staff permissions saved${r.data.updated_staff ? ` · applied to ${r.data.updated_staff} staff` : ""}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400 p-6"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  const groups = GROUPS.map((g) => ({ ...g, perms: g.perms.filter((p) => all.includes(p)) })).filter((g) => g.perms.length);

  return (
    <div className="space-y-5" data-testid="staff-permissions-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={18} className="text-indigo-500" />Staff App Permissions</h3>
          <p className="text-sm text-slate-500">Choose what Staff accounts can access and do in the app. Saving applies these to all existing Staff accounts and to every new staff member you add.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-staff-permissions-btn">{saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}Save Staff Permissions</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {groups.map((g) => {
          const allOn = g.perms.every((p) => perms.includes(p));
          return (
            <div key={g.title} className="border border-slate-200 rounded-xl p-3 bg-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-indigo-500">{g.title}</span>
                <button type="button" onClick={() => setGroup(g.perms, !allOn)} className="text-[11px] text-slate-400 hover:text-indigo-600" data-testid={`staff-perm-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{allOn ? "Clear" : "Select all"}</button>
              </div>
              <div className="space-y-0.5">
                {g.perms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={perms.includes(p)} onCheckedChange={() => toggle(p)} data-testid={`staff-perm-${p}`} />
                    {labelize(p)}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200 pt-5" data-testid="staff-bulk-fields-section">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Staff Bulk-Edit Fields</h3>
            <p className="text-sm text-slate-500">Pick which student fields Staff may edit on the Bulk Edit page. Everything is off by default — staff can only change the fields you enable here, and only for students in their assigned classes.</p>
          </div>
          <Button onClick={saveBulk} disabled={savingBulk} variant="secondary" data-testid="save-staff-bulk-fields-btn">{savingBulk ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}Save Bulk-Edit Fields</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1 border border-slate-200 rounded-xl p-3 bg-white">
          {BULK_FIELDS.map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-xs p-1 hover:bg-slate-50 rounded cursor-pointer">
              <Checkbox checked={bulkFields.includes(k)} onCheckedChange={() => toggleBulk(k)} data-testid={`staff-bulkfield-${k}`} />
              {lbl}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
